import { useEffect, useMemo, useState } from "react";

import {
  colorNames,
  colorPalette,
  freeColorIds,
  paidColorIds,
  type ColorId,
} from "../design/productConfig";
import { analytics } from "../services/analytics/analytics";
import { errors } from "../services/errors/errors";
import type {
  GarageApi,
  GarageCustomization,
} from "../services/garage-api/GarageApi";
import { MockGarageApi } from "../services/garage-api/MockGarageApi";
import { NetlifyGarageApi } from "../services/garage-api/NetlifyGarageApi";
import { useTrafficStore } from "../stores/useTrafficStore";
import {
  FREE_VEHICLE_PRESETS,
  HORN_IDS,
  PAID_VEHICLE_PRESETS,
  type DriverProfile,
  type FreeVehiclePreset,
  type HornId,
  type VehiclePreset,
} from "../types/traffic";
import { Icon } from "./Icons";
import { ModalShell } from "./ModalShell";
import { VehicleArt } from "./VehicleArt";

const liveGarageApi = new NetlifyGarageApi();
const mockGarageApi = new MockGarageApi();

function garageApiFor(
  transportMode: "mock" | "supabase" | "offline",
): GarageApi {
  return transportMode === "mock" ? mockGarageApi : liveGarageApi;
}

const hornNames: Record<HornId, string> = {
  classic: "Classic",
  courtesy: "Courtesy Beep",
  foghorn: "Foghorn",
  scooter: "Scooter",
  melody: "Tiny Melody",
};

function customizationFor(profile: DriverProfile): GarageCustomization {
  return {
    vehiclePreset: profile.vehiclePreset,
    colorId: profile.colorId,
    hornId: profile.hornId,
    plate: profile.plate,
  };
}

function customizationMatches(
  draft: GarageCustomization,
  profile: DriverProfile,
): boolean {
  return (
    draft.vehiclePreset === profile.vehiclePreset &&
    draft.colorId === profile.colorId &&
    draft.hornId === profile.hornId &&
    draft.plate === profile.plate
  );
}

function hasPremiumChoice(
  draft: GarageCustomization,
  profile: DriverProfile,
): boolean {
  return (
    PAID_VEHICLE_PRESETS.includes(
      draft.vehiclePreset as (typeof PAID_VEHICLE_PRESETS)[number],
    ) ||
    paidColorIds.includes(draft.colorId as (typeof paidColorIds)[number]) ||
    draft.hornId !== "classic" ||
    draft.plate !== profile.plate
  );
}

export function GarageModal() {
  const open = useTrafficStore((state) => state.garageOpen);
  const profile = useTrafficStore((state) => state.profile);
  const setOpen = useTrafficStore((state) => state.setGarageOpen);
  const replaceProfile = useTrafficStore((state) => state.replaceProfile);
  const transportMode = useTrafficStore((state) => state.transportMode);
  const [purchaseKey, setPurchaseKey] = useState<string | null>(null);
  const [returnMessage, setReturnMessage] = useState("");
  const [unlockSucceeded, setUnlockSucceeded] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    const parameters = new URLSearchParams(location.search);
    const checkout = parameters.get("checkout");
    if (!checkout) return;
    const cleanUrl = `${location.pathname}${location.hash}`;
    if (checkout === "cancelled") {
      queueMicrotask(() => {
        setOpen(true);
        setReturnMessage("Checkout cancelled. Nothing was charged.");
      });
      history.replaceState(history.state, "", cleanUrl);
      return;
    }
    const sessionId = parameters.get("session_id");
    if (!sessionId) return;
    queueMicrotask(() => {
      setOpen(true);
      setVerifying(true);
    });
    void garageApiFor(transportMode)
      .verifyPurchase(sessionId)
      .then((result) => {
        replaceProfile(result.profile);
        setPurchaseKey(result.garageKey);
        setUnlockSucceeded(true);
        setReturnMessage(
          "Garage Pack unlocked. Your selected customization was applied.",
        );
        analytics.track("checkout_completed", {
          productId: "garage_pack_v1",
          amount: 499,
          currency: "usd",
        });
      })
      .catch((error: unknown) => {
        errors.capture(error, { action: "verify_garage_purchase" });
        setReturnMessage(
          "Payment verification is still pending. Reopen the Garage shortly.",
        );
      })
      .finally(() => {
        setVerifying(false);
        history.replaceState(history.state, "", cleanUrl);
      });
  }, [replaceProfile, setOpen, transportMode]);

  if (!open) return null;

  if (unlockSucceeded && profile) {
    return (
      <ModalShell
        eyebrow="PAYMENT CONFIRMED"
        title="Garage Pack unlocked!"
        headerBadge="OWNED"
        className="garage-modal garage-unlock-modal"
        onClose={() => {
          setUnlockSucceeded(false);
          setOpen(false);
        }}
      >
        <GarageUnlockConfirmation
          recoveryCode={purchaseKey}
          transportMode={transportMode}
          onContinue={() => setUnlockSucceeded(false)}
        />
      </ModalShell>
    );
  }

  return (
    <ModalShell
      eyebrow="YOUR VEHICLE"
      title="The Garage"
      {...(profile?.garageOwned ? { headerBadge: "OWNED" } : {})}
      className="garage-modal"
      onClose={() => setOpen(false)}
    >
      {profile ? (
        <GarageContent
          key={`${profile.publicDriverId}:${profile.garageOwned}`}
          profile={profile}
          transportMode={transportMode}
          purchaseKey={purchaseKey}
          returnMessage={returnMessage}
          verifying={verifying}
          onPurchaseKey={setPurchaseKey}
        />
      ) : (
        <p>Merge into traffic to receive your free vehicle.</p>
      )}
    </ModalShell>
  );
}

type GarageContentProps = {
  profile: DriverProfile;
  transportMode: "mock" | "supabase" | "offline";
  purchaseKey: string | null;
  returnMessage: string;
  verifying: boolean;
  onPurchaseKey: (key: string | null) => void;
};

function GarageContent({
  profile,
  transportMode,
  purchaseKey,
  returnMessage,
  verifying,
  onPurchaseKey,
}: GarageContentProps) {
  const replaceProfile = useTrafficStore((state) => state.replaceProfile);
  const setOpen = useTrafficStore((state) => state.setGarageOpen);
  const selectFreeVehicle = useTrafficStore((state) => state.selectFreeVehicle);
  const selectFreeColor = useTrafficStore((state) => state.selectFreeColor);
  const [draft, setDraft] = useState<GarageCustomization>(() =>
    customizationFor(profile),
  );
  const [view, setView] = useState<"customize" | "recovery">("customize");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(returnMessage);
  const garageApi = garageApiFor(transportMode);
  const selectedColor = draft.colorId as ColorId;
  const selectedColorHex =
    selectedColor in colorPalette
      ? `#${colorPalette[selectedColor].toString(16).padStart(6, "0")}`
      : "#ff5a1f";
  const purchasesAvailable = transportMode !== "offline";
  const dirty = !customizationMatches(draft, profile);
  const premiumChoice = hasPremiumChoice(draft, profile);

  const updateDraft = <Key extends keyof GarageCustomization>(
    key: Key,
    value: GarageCustomization[Key],
  ) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setMessage("");
  };

  const checkout = async () => {
    setBusy(true);
    setMessage("");
    try {
      const url = await garageApi.createCheckout(draft);
      analytics.track("checkout_started", { productId: "garage_pack_v1" });
      location.assign(url);
    } catch (error) {
      errors.capture(error, { action: "create_garage_checkout" });
      setMessage(
        error instanceof Error ? error.message : "Checkout is unavailable.",
      );
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setMessage("");
    try {
      if (profile.garageOwned) {
        const updated = await garageApi.save(draft);
        replaceProfile(updated);
        setDraft(customizationFor(updated));
      } else {
        selectFreeVehicle(draft.vehiclePreset as FreeVehiclePreset);
        selectFreeColor(draft.colorId as ColorId);
      }
      setOpen(false);
    } catch (error) {
      errors.capture(error, { action: "save_garage" });
      setMessage(error instanceof Error ? error.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  };

  const action = useMemo(() => {
    if (!dirty) return { label: "Saved", kind: "saved" as const };
    if (!profile.garageOwned && premiumChoice) {
      return {
        label:
          transportMode === "mock"
            ? "Unlock & apply · Demo checkout"
            : "Unlock & apply · $4.99",
        kind: "checkout" as const,
      };
    }
    return {
      label: profile.garageOwned ? "Save Garage changes" : "Save free changes",
      kind: "save" as const,
    };
  }, [dirty, premiumChoice, profile.garageOwned, transportMode]);

  if (view === "recovery") {
    return (
      <RecoveryView
        api={garageApi}
        owned={profile.garageOwned}
        purchasesAvailable={purchasesAvailable}
        purchaseKey={purchaseKey}
        onBack={() => setView("customize")}
        onPurchaseKey={onPurchaseKey}
        onRestored={(updated) => {
          replaceProfile(updated);
          setMessage("Garage Pack restored to this browser.");
          setView("customize");
        }}
      />
    );
  }

  return (
    <>
      <div className="garage-identity">
        <VehicleArt
          className="garage-vehicle-art"
          preset={draft.vehiclePreset}
          color={selectedColorHex}
        />
        <div>
          <span className="garage-identity__plate">{draft.plate}</span>
          <strong>{draft.vehiclePreset.replaceAll("-", " ")}</strong>
          <span>{colorNames[selectedColor]}</span>
          {profile.garageOwned ? (
            <span className="garage-owned-label">Garage Pack owned</span>
          ) : null}
        </div>
      </div>

      <section className="garage-tier garage-tier--included">
        <header>
          <div>
            <span className="eyebrow">INCLUDED</span>
            <h3>Free Garage</h3>
          </div>
          <small>Always available</small>
        </header>
        <VehiclePicker
          legend="Included vehicles"
          presets={FREE_VEHICLE_PRESETS}
          selected={draft.vehiclePreset}
          color={selectedColorHex}
          onSelect={(preset) => updateDraft("vehiclePreset", preset)}
        />
        <ColorPicker
          legend="Included paint"
          colorIds={freeColorIds}
          selected={draft.colorId}
          onSelect={(colorId) => updateDraft("colorId", colorId)}
        />
      </section>

      <section className="garage-tier garage-tier--premium">
        <header>
          <div>
            <span className="eyebrow">GARAGE PACK · $4.99 · ONE TIME</span>
            <h3>Premium customization</h3>
          </div>
          <span className="garage-tier__status">
            {profile.garageOwned ? "OWNED" : "PREMIUM"}
          </span>
        </header>
        <p className="garage-tier__lede">
          Six more vehicle styles, seven paints, four horns, a custom plate, and
          a permanent badge. Exactly the same speed.
        </p>
        {transportMode === "mock" && !profile.garageOwned ? (
          <p className="garage-demo-note">
            Demo purchase—stored only in this browser.
          </p>
        ) : null}
        <VehiclePicker
          legend="Premium vehicles"
          presets={PAID_VEHICLE_PRESETS}
          selected={draft.vehiclePreset}
          color={selectedColorHex}
          premium
          locked={!profile.garageOwned}
          onSelect={(preset) => updateDraft("vehiclePreset", preset)}
        />
        <ColorPicker
          legend="Premium paint"
          colorIds={paidColorIds}
          selected={draft.colorId}
          premium
          locked={!profile.garageOwned}
          onSelect={(colorId) => updateDraft("colorId", colorId)}
        />
        <fieldset className="horn-picker">
          <legend>Horn</legend>
          <div>
            {HORN_IDS.map((hornId) => {
              const premium = hornId !== "classic";
              return (
                <button
                  key={hornId}
                  type="button"
                  className={draft.hornId === hornId ? "is-selected" : ""}
                  aria-pressed={draft.hornId === hornId}
                  aria-label={`${hornNames[hornId]}${premium && !profile.garageOwned ? " · Premium preview" : ""}`}
                  onClick={() => updateDraft("hornId", hornId)}
                >
                  {hornNames[hornId]}
                  <small>
                    {premium ? (
                      <>
                        {!profile.garageOwned ? <Icon name="lock" /> : null}
                        PREMIUM
                      </>
                    ) : (
                      "INCLUDED"
                    )}
                  </small>
                </button>
              );
            })}
          </div>
        </fieldset>

        <label className="garage-plate-field">
          <span>
            Custom plate
            <PremiumLabel locked={!profile.garageOwned} />
          </span>
          <small>1–7 letters, numbers, or one space</small>
          <input
            value={draft.plate}
            maxLength={7}
            autoCapitalize="characters"
            aria-label="Custom plate"
            onChange={(event) =>
              updateDraft("plate", event.target.value.toUpperCase())
            }
          />
        </label>
      </section>

      {purchaseKey ? (
        <RecoveryCodeCard recoveryCode={purchaseKey} onMessage={setMessage} />
      ) : null}

      <section className="garage-recovery-summary">
        <div>
          <strong>
            {profile.garageOwned
              ? "Protect your purchase"
              : "Already purchased?"}
          </strong>
          <span>
            {profile.garageOwned
              ? "Create a Recovery Code and write it down somewhere safe."
              : "Enter your saved Recovery Code on this device."}
          </span>
        </div>
        <button
          className="button button--quiet"
          type="button"
          disabled={!purchasesAvailable}
          onClick={() => setView("recovery")}
        >
          {profile.garageOwned ? "Recovery options" : "Restore purchase"}
        </button>
      </section>

      <div className="garage-action-bar">
        <div>
          <strong>
            {action.kind === "checkout"
              ? "Premium choices ready"
              : dirty
                ? "Unsaved changes"
                : "Your vehicle is up to date"}
          </strong>
          <span>
            {message ||
              (action.kind === "checkout"
                ? "Unlock once to apply this premium setup."
                : "Changes apply only when you save.")}
          </span>
        </div>
        <button
          className="button button--primary"
          type="button"
          disabled={
            busy ||
            verifying ||
            action.kind === "saved" ||
            (action.kind === "checkout" && !purchasesAvailable)
          }
          onClick={() =>
            void (action.kind === "checkout" ? checkout() : save())
          }
        >
          {busy || verifying ? "Working…" : action.label}
        </button>
      </div>
    </>
  );
}

type VehiclePickerProps = {
  legend: string;
  presets: readonly VehiclePreset[];
  selected: VehiclePreset;
  color: string;
  premium?: boolean;
  locked?: boolean;
  onSelect: (preset: VehiclePreset) => void;
};

function VehiclePicker({
  legend,
  presets,
  selected,
  color,
  premium = false,
  locked = false,
  onSelect,
}: VehiclePickerProps) {
  return (
    <fieldset className="vehicle-picker">
      <legend>{legend}</legend>
      <div>
        {presets.map((preset) => (
          <button
            key={preset}
            type="button"
            className={selected === preset ? "is-selected" : ""}
            aria-pressed={selected === preset}
            aria-label={`${preset.replaceAll("-", " ")}${premium && locked ? " · Premium preview" : ""}`}
            onClick={() => onSelect(preset)}
          >
            <VehicleArt
              className="vehicle-option-art"
              preset={preset}
              color={color}
            />
            <span>{preset.replaceAll("-", " ")}</span>
            <small>
              {premium ? (
                <>
                  {locked ? <Icon name="lock" /> : null}
                  PREMIUM
                </>
              ) : selected === preset ? (
                "SELECTED"
              ) : (
                "INCLUDED"
              )}
            </small>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

type ColorPickerProps = {
  legend: string;
  colorIds: readonly ColorId[];
  selected: string;
  premium?: boolean;
  locked?: boolean;
  onSelect: (colorId: ColorId) => void;
};

function ColorPicker({
  legend,
  colorIds,
  selected,
  premium = false,
  locked = false,
  onSelect,
}: ColorPickerProps) {
  return (
    <fieldset className="color-picker">
      <legend>{legend}</legend>
      <div>
        {colorIds.map((colorId) => (
          <button
            key={colorId}
            type="button"
            className={selected === colorId ? "is-selected" : ""}
            aria-label={`${colorNames[colorId]}${premium && locked ? " · Premium preview" : ""}`}
            aria-pressed={selected === colorId}
            onClick={() => onSelect(colorId)}
          >
            <span
              style={{
                backgroundColor: `#${colorPalette[colorId]
                  .toString(16)
                  .padStart(6, "0")}`,
              }}
              aria-hidden="true"
            />
            <small>{colorNames[colorId]}</small>
            {premium ? <PremiumLabel locked={locked} /> : null}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function PremiumLabel({ locked }: { locked: boolean }) {
  return (
    <em className="premium-label">
      {locked ? <Icon name="lock" /> : null}
      PREMIUM
    </em>
  );
}

function GarageUnlockConfirmation({
  recoveryCode,
  transportMode,
  onContinue,
}: {
  recoveryCode: string | null;
  transportMode: "mock" | "supabase" | "offline";
  onContinue: () => void;
}) {
  const [message, setMessage] = useState("");

  return (
    <div className="garage-unlock-confirmation">
      <section className="garage-success" aria-live="polite">
        <span className="eyebrow">CONGRATULATIONS</span>
        <h3>You own the Garage Pack.</h3>
        <p>
          Your selected customization is applied. Premium vehicles, paints,
          horns, custom plates, and your permanent Garage badge are unlocked.
        </p>
        {transportMode === "mock" ? (
          <small>Demo purchase—stored only in this browser.</small>
        ) : null}
      </section>
      {recoveryCode ? (
        <RecoveryCodeCard recoveryCode={recoveryCode} onMessage={setMessage} />
      ) : (
        <p className="garage-message">
          You can create a Recovery Code later from Recovery options.
        </p>
      )}
      <button
        className="button button--primary"
        type="button"
        onClick={onContinue}
      >
        Continue to your Garage
      </button>
      <p className="garage-message" aria-live="polite">
        {message}
      </p>
    </div>
  );
}

function RecoveryCodeCard({
  recoveryCode,
  onMessage,
}: {
  recoveryCode: string;
  onMessage: (message: string) => void;
}) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCode);
      onMessage("Recovery Code copied.");
    } catch {
      onMessage("Copy failed. Select the Recovery Code and copy it manually.");
    }
  };
  return (
    <section className="garage-key" aria-live="polite">
      <span className="eyebrow">SAVE THIS RECOVERY CODE</span>
      <strong>{recoveryCode}</strong>
      <p>
        Write this down and save it somewhere safe. It is shown only once. On a
        new browser, it moves Garage Pack ownership there; mileage and trip
        history remain with each browser.
      </p>
      <button
        className="button button--quiet"
        type="button"
        onClick={() => void copy()}
      >
        Copy Recovery Code
      </button>
    </section>
  );
}

type RecoveryViewProps = {
  api: GarageApi;
  owned: boolean;
  purchasesAvailable: boolean;
  purchaseKey: string | null;
  onBack: () => void;
  onPurchaseKey: (key: string | null) => void;
  onRestored: (profile: DriverProfile) => void;
};

function RecoveryView({
  api,
  owned,
  purchasesAvailable,
  purchaseKey,
  onBack,
  onPurchaseKey,
  onRestored,
}: RecoveryViewProps) {
  const [recoveryCode, setRecoveryCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const restoreWithCode = async () => {
    setBusy(true);
    setMessage("");
    try {
      onRestored(await api.restore(recoveryCode));
      analytics.track("garage_restored", {
        result: "success",
        method: "recovery_code",
      });
    } catch (error) {
      errors.capture(error, { action: "restore_garage_recovery_code" });
      analytics.track("garage_restored", {
        result: "failed",
        method: "recovery_code",
      });
      setMessage("Garage Pack could not be restored.");
    } finally {
      setBusy(false);
    }
  };

  const rotateCode = async () => {
    setBusy(true);
    setMessage("");
    try {
      onPurchaseKey(await api.rotateKey());
      setMessage(
        "New Recovery Code created. The previous code no longer works.",
      );
    } catch (error) {
      errors.capture(error, { action: "rotate_garage_recovery_code" });
      setMessage("Recovery Code creation failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="garage-recovery-view">
      <button className="button button--quiet" type="button" onClick={onBack}>
        ← Back to Garage
      </button>
      <header>
        <span className="eyebrow">PURCHASE RECOVERY</span>
        <h3>
          {owned ? "Protect your Garage Pack" : "Restore your Garage Pack"}
        </h3>
        <p>
          No account or password required. Restoring moves Garage Pack ownership
          to this browser. Mileage and trip history do not move.
        </p>
      </header>

      <section className="garage-recovery-method">
        <span className="eyebrow">RECOVERY CODE</span>
        {owned ? (
          <>
            <p>
              Create a five-word code, then write it down and save it somewhere
              safe. Creating a new code invalidates the previous one.
            </p>
            <button
              className="button button--quiet"
              type="button"
              disabled={busy}
              onClick={() => void rotateCode()}
            >
              Create new Recovery Code
            </button>
            {purchaseKey ? (
              <RecoveryCodeCard
                recoveryCode={purchaseKey}
                onMessage={setMessage}
              />
            ) : null}
          </>
        ) : (
          <>
            <label>
              <span>Recovery Code</span>
              <input
                value={recoveryCode}
                autoCapitalize="characters"
                autoComplete="off"
                placeholder="HORSE-BROWN-HOUSE-DANCE-SKY"
                onChange={(event) =>
                  setRecoveryCode(event.target.value.toUpperCase())
                }
              />
            </label>
            <button
              className="button button--quiet"
              type="button"
              disabled={!purchasesAvailable || busy || recoveryCode.length < 16}
              onClick={() => void restoreWithCode()}
            >
              Restore with Recovery Code
            </button>
          </>
        )}
      </section>
      <p className="garage-message" aria-live="polite">
        {message}
      </p>
    </section>
  );
}
